import React, { useEffect, useMemo, useState } from 'react';
import { Menu, MenuItem, Collapse } from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import { useHistory } from 'react-router';
import UnfoldMoreIcon from '@material-ui/icons/UnfoldMore';
import HomeIcon from '@material-ui/icons/Home';
import DateRangeIcon from '@material-ui/icons/DateRange';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import RoomServiceIcon from '@material-ui/icons/RoomService';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ApartmentIcon from '@material-ui/icons/Apartment';
import LanguageIcon from '@material-ui/icons/Language';
import WeekendIcon from '@material-ui/icons/Weekend';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import BarChartIcon from '@material-ui/icons/BarChart';
import PeopleIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import SettingsIcon from '@material-ui/icons/Settings';
import PersonIcon from '@material-ui/icons/Person';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { useTheme } from '@material-ui/core/styles';

import { SETTINGS_ROUTES } from '../../../config/routes-config';
import {
  SIDE_MENU_PINNED,
  SIDE_MENU_SECTIONS,
  canSeeRoute,
  findActiveItem,
  getItemLabel,
  getVisibleSections
} from '../../../config/side-menu.config';
import type { SideMenuIcon, SideMenuItem, SideMenuSection } from '../../../config/side-menu.config';
import type { RouteConfigItem } from '../../../interfaces/routes-config.interface';
import type { UserNotificationsModel } from '../../../models/user-notifications.model';
import type { UserRole } from '../../../enums/user-role.enum';

const sectionIcons: { [key in SideMenuIcon]: React.ElementType } = {
  home: HomeIcon,
  calendar: DateRangeIcon,
  reservations: EventAvailableIcon,
  operations: RoomServiceIcon,
  sales: TrendingUpIcon,
  properties: ApartmentIcon,
  marketing: LanguageIcon,
  furnishing: WeekendIcon,
  finance: AccountBalanceIcon,
  insights: BarChartIcon,
  users: PeopleIcon
};

const settingsIcons: { [key: string]: React.ElementType } = {
  configuration: SettingsIcon,
  'user profile': PersonIcon,
  logout: ExitToAppIcon
};

/**
 * Which sections the user has opened or closed, kept across page loads. Every route renders its
 * own layout, so without this the menu would forget the user's choices on each navigation.
 * Storage can be unavailable (private mode, blocked site data); the menu then just starts fresh.
 */
const EXPANDED_STORAGE_KEY = 'birdnest.admin.sideMenu.expanded';

const readStoredExpanded = (): { [key: string]: boolean } => {
  try {
    return JSON.parse(window.localStorage.getItem(EXPANDED_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeStoredExpanded = (expanded: { [key: string]: boolean }) => {
  try {
    window.localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(expanded));
  } catch {
    // Not persisting is harmless: the section still toggles for this page.
  }
};

export interface SideMenuProps {
  userEmail?: string;
  userImage?: string;
  userRole: UserRole;
  userRoles: UserRole[];
  userNotification?: UserNotificationsModel;
  toggleSideMenu?: boolean;
  logoutHandler?: () => void;
}

export const SideMenu: React.FC<SideMenuProps> = ({
  userEmail,
  userImage,
  userRole,
  userRoles,
  userNotification,
  toggleSideMenu,
  logoutHandler
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>(
    readStoredExpanded
  );
  const [searchTerm, setSearchTerm] = useState('');
  const history = useHistory();
  const pathname = history.location.pathname;

  useEffect(() => {
    setMobileOpen(toggleSideMenu || false);
  }, [toggleSideMenu]);

  const pinnedItems = useMemo(
    () => getVisibleSections(SIDE_MENU_PINNED, userRole, userRoles),
    [userRole, userRoles]
  );
  const sections = useMemo(
    () => getVisibleSections(SIDE_MENU_SECTIONS, userRole, userRoles),
    [userRole, userRoles]
  );
  const settingsMenuItemsList = useMemo(
    () =>
      Object.keys(SETTINGS_ROUTES)
        .map((key) => SETTINGS_ROUTES[key])
        .filter((route) => route.IS_MENU && canSeeRoute(route, userRole, userRoles)),
    [userRole, userRoles]
  );

  const activeItem = findActiveItem([...pinnedItems, ...sections], pathname);
  const isItemActive = (item: SideMenuItem) => activeItem?.route === item.route;
  const isSectionActive = (section: SideMenuSection) => section.items.some(isItemActive);

  const searchResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return [];
    return [...pinnedItems, ...sections].flatMap((section) =>
      section.items
        .filter(
          (item) =>
            getItemLabel(item).toLowerCase().includes(term) ||
            section.label.toLowerCase().includes(term)
        )
        .map((item) => ({ item, section }))
    );
  }, [searchTerm, pinnedItems, sections]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleLogout = () => {
    handleClose();
    if (logoutHandler) logoutHandler();
  };
  const handleClick = (event: React.SyntheticEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * function to handle closing user menu
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigate = (route: RouteConfigItem) => {
    setSearchTerm('');
    history.push(route.FULL_PATH);
  };

  const getNotificationCount = (route: RouteConfigItem): number => {
    if (!route.NOTIFICATIONS_KEY || !userNotification) return 0;
    const count = userNotification[route.NOTIFICATIONS_KEY as keyof UserNotificationsModel];
    return typeof count === 'number' && count > 0 ? count : 0;
  };

  const getSectionNotificationCount = (section: SideMenuSection) =>
    section.items.reduce((total, item) => total + getNotificationCount(item.route), 0);

  /**
   * A section is open when the user has said it is. Until they touch it, the section holding the
   * current route starts open as a convenience.
   *
   * The distinction matters: reading this as `expandedSections[key] || isSectionActive(section)`
   * makes the section you are standing in impossible to close, because the active route keeps
   * forcing it back open whatever the toggle stored. `undefined` here means "never touched",
   * which is why it is checked rather than coerced.
   */
  const isSectionExpanded = (section: SideMenuSection) => {
    const explicitlySet = expandedSections[section.key];
    return explicitlySet === undefined ? isSectionActive(section) : explicitlySet;
  };

  /**
   * Toggled against what is on screen rather than against the stored flag: a section auto-opened
   * by the active route has no stored flag yet, and negating `undefined` would re-open an
   * already-open section on the first click.
   */
  const handleSectionToggle = (section: SideMenuSection) => {
    const next = { ...expandedSections, [section.key]: !isSectionExpanded(section) };
    setExpandedSections(next);
    writeStoredExpanded(next);
  };

  const renderBadge = (count: number) =>
    count > 0 ? (
      <span className="min-w-[24px] h-6 px-1.5 ml-auto flex items-center justify-center bg-primary-500 text-white rounded-full text-xs font-bold">
        {count}
      </span>
    ) : null;

  const renderNewLabel = (route: RouteConfigItem) =>
    route.NEW_LABEL ? (
      <span className="text-sm border-primary-500 border bg-primary-50 font-gilroy-bold text-primary-500 font-bold rounded-lg px-3 py-1 ml-auto uppercase tracking-wide">
        NEW
      </span>
    ) : null;

  /** A top-level row: a pinned page, a section header, or a section reduced to one page. */
  const renderTopLevelRow = (
    icon: SideMenuIcon,
    label: string,
    active: boolean,
    onClick: () => void,
    trailing?: React.ReactNode
  ) => {
    const Icon = sectionIcons[icon];
    return (
      <button
        type="button"
        className={`w-full group flex items-center rounded-2xl px-3 py-2.5 transition-all duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-100 ${
          active
            ? 'bg-primary-50 text-primary-600 shadow-sm'
            : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
        }`}
        onClick={onClick}
      >
        <Icon
          className={`h-7 w-7 mr-4 transition-colors duration-200 ${
            active ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-500'
          }`}
        />
        <span
          className={`text-lg md:text-2xl font-medium font-gilroy-medium ${
            active ? 'text-primary-700 font-semibold' : 'text-gray-600 group-hover:text-gray-900'
          }`}
        >
          {label}
        </span>
        {trailing}
      </button>
    );
  };

  const renderSubItem = (item: SideMenuItem, caption?: string) => {
    const active = isItemActive(item);
    return (
      <li key={item.route.FULL_PATH}>
        <button
          type="button"
          className={`w-full flex items-center text-left pl-3 pr-2 py-2.5 rounded-xl transition-all duration-200 ${
            active
              ? 'bg-white text-primary-700 font-semibold shadow-sm'
              : 'text-gray-500 hover:bg-white hover:text-gray-900'
          }`}
          onClick={() => navigate(item.route)}
        >
          <span className="flex flex-col">
            <span className="text-base md:text-xl font-gilroy-medium">{getItemLabel(item)}</span>
            {caption && (
              <span className="text-sm text-gray-400 font-gilroy-medium uppercase tracking-wide">
                {caption}
              </span>
            )}
          </span>
          {renderNewLabel(item.route)}
          {renderBadge(getNotificationCount(item.route))}
        </button>
      </li>
    );
  };

  /**
   * A role that can open only one page in a section gets that page as a plain link: a section
   * header that opens onto a single entry is a click spent on nothing.
   */
  const renderSection = (section: SideMenuSection) => {
    if (section.items.length === 1) {
      const [item] = section.items;
      return (
        <li key={section.key}>
          {renderTopLevelRow(
            section.icon,
            getItemLabel(item),
            isItemActive(item),
            () => navigate(item.route),
            <>
              {renderNewLabel(item.route)}
              {renderBadge(getNotificationCount(item.route))}
            </>
          )}
        </li>
      );
    }

    const expanded = isSectionExpanded(section);
    const active = isSectionActive(section);
    return (
      <li key={section.key}>
        {renderTopLevelRow(
          section.icon,
          section.label,
          active,
          () => handleSectionToggle(section),
          <>
            {!expanded && renderBadge(getSectionNotificationCount(section))}
            {expanded ? (
              <ExpandLessIcon
                className="text-primary-500 ml-auto transition-transform duration-200"
                style={{ fontSize: 20 }}
              />
            ) : (
              <ExpandMoreIcon
                className={`text-gray-400 group-hover:text-primary-500 transition-transform duration-200 ${
                  getSectionNotificationCount(section) > 0 ? 'ml-2' : 'ml-auto'
                }`}
                style={{ fontSize: 20 }}
              />
            )}
          </>
        )}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <ul className="p-1 border-l-2 border-primary-100 ml-7 pl-3 space-y-1 my-1.5 bg-gray-50/60 rounded-r-2xl">
            {section.items.map((item) => renderSubItem(item))}
          </ul>
        </Collapse>
      </li>
    );
  };

  const drawer = (
    <div className="flex flex-col h-full box-border bg-white">
      <div className="px-6 pt-6 pb-3">
        <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 focus-within:border-primary-300 focus-within:bg-white transition-colors">
          <SearchIcon className="text-gray-400" style={{ fontSize: 20 }} />
          <input
            type="text"
            value={searchTerm}
            placeholder="Find a page"
            aria-label="Find a page"
            className="flex-1 min-w-0 bg-transparent text-base md:text-lg font-gilroy-medium text-gray-700 placeholder-gray-400 focus:outline-none"
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && searchResults.length > 0) {
                navigate(searchResults[0].item.route);
              }
              if (event.key === 'Escape') {
                setSearchTerm('');
              }
            }}
          />
          {searchTerm && (
            <button
              type="button"
              aria-label="Clear search"
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => setSearchTerm('')}
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          )}
        </label>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-6">
        {searchTerm.trim() ? (
          searchResults.length > 0 ? (
            <ul className="p-1 space-y-1">
              {searchResults.map(({ item, section }) => renderSubItem(item, section.label))}
            </ul>
          ) : (
            <div className="px-3 py-6 text-base text-gray-400 font-gilroy-medium">
              No pages match “{searchTerm.trim()}”
            </div>
          )
        ) : (
          <>
            <ul className="p-0 space-y-1">{pinnedItems.map(renderSection)}</ul>
            {sections.length > 0 && (
              <>
                <div className="px-3 pt-5 pb-2">
                  <div className="text-base font-bold text-gray-400 uppercase tracking-widest">
                    Modules
                  </div>
                </div>
                <ul className="p-0 space-y-1">{sections.map(renderSection)}</ul>
              </>
            )}
          </>
        )}
      </div>

      {userEmail && (
        <div className="p-3 border-t border-gray-100 bg-white">
          <button
            type="button"
            className="w-full flex items-center p-2 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
            onClick={handleClick}
          >
            {userImage ? (
              <img
                alt="user-avatar"
                src={userImage}
                className="w-10 h-10 rounded-full mr-3 border border-gray-200"
              />
            ) : (
              <div className="w-10 h-10 rounded-full mr-3 bg-primary-100 text-primary-600 flex items-center justify-center text-base font-bold border border-primary-200">
                {userEmail?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 text-start overflow-hidden mr-2">
              <h4 className="m-0 text-base font-bold text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis font-gilroy-bold capitalize">
                {userEmail?.split('@')[0]}
              </h4>
              <span className="text-sm text-gray-500 block whitespace-nowrap overflow-hidden text-ellipsis font-gilroy-medium">
                {userEmail}
              </span>
            </div>
            <UnfoldMoreIcon
              className="text-gray-400 group-hover:text-gray-600"
              style={{ fontSize: 22 }}
            />
          </button>
        </div>
      )}

      <Menu
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          style: {
            minWidth: '220px',
            marginTop: '-8px',
            borderRadius: '12px',
            overflow: 'hidden'
          }
        }}
      >
        {settingsMenuItemsList.map((listItem) => {
          const Icon = settingsIcons[listItem.NAME];
          return (
            <MenuItem
              key={listItem.NAME}
              className="py-3 px-4 gap-3 hover:bg-gray-50 transition-colors"
              onClick={() => {
                if (listItem.NAME === 'logout') {
                  handleLogout();
                } else {
                  history.push(listItem.FULL_PATH);
                  handleClose();
                }
              }}
            >
              {Icon ? (
                <Icon className="w-5 h-5 object-contain text-gray-500" />
              ) : (
                listItem.ICON && (
                  <img
                    alt={`icon-${listItem.NAME}`}
                    className="w-5 h-5 object-contain text-gray-500"
                    src={listItem.ICON}
                  />
                )
              )}
              <span className="flex-1 text-base font-medium text-gray-700">
                {listItem.DISPLAY_NAME}
              </span>
              {renderNewLabel(listItem)}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );

  return (
    <nav className="sm:w-96 md:w-96 sm:flex-shrink-0 md:flex-shrink-0" aria-label="mailbox folders">
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      <Drawer
        variant="temporary"
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        classes={{
          paper: 'sm:w-96 md:w-96 bg-white border-r border-gray-200'
        }}
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}
      >
        {drawer}
      </Drawer>
    </nav>
  );
};
